// imperial-planet/legacy — the 3rdgen renderer wrapped in the Planet class.
//
// This is the ESCAPE HATCH: the game's current, hardware-proven planet. It
// implements the same Planet contract as ./next so the game can fall back with
// a one-line import swap if the new renderer is too heavy or hard to maintain.
//
// Under the hood it is the synchronous buildPlanetMesh() (no worker, no GPU
// bake). See ../common/planet-interface.js for the contract.

import { Vector3, Quaternion } from 'three';
import { buildPlanetMesh } from './planet-3rdgen.js';

const _UP = new Vector3(0, 1, 0);

function disposeObject(root) {
	root.traverse(o => {
		if (o.geometry) o.geometry.dispose();
		const mats = Array.isArray(o.material) ? o.material : o.material ? [o.material] : [];
		for (const m of mats) {
			for (const k in m) { const v = m[k]; if (v && v.isTexture) v.dispose(); }
			m.dispose?.();
		}
	});
}

export class Planet {
	/** @param {import('../common/planet-interface.js').PlanetSpec} spec */
	constructor(spec = {}, opts = {}) {
		this.spec = spec;
		this.opts = opts;
		this._m = null;      // buildPlanetMesh result
		this._grid = null;   // lazy grid LineSegments
	}

	async build() {
		this._m = buildPlanetMesh(this.spec, this.opts);
		return this;
	}

	get object3d() { return this._m.group; }
	get planetMesh() { return this._m.planetMesh; }
	get atmosphere() { return this._m.atmosphere; }
	get uniforms() { return this._m.uniforms; }
	get tileCount() { return this._m.centers.length; }
	get faceIndex() { return this._m.faceIndex; }
	get neighbors() { return this._m.neighbors; }
	get boundaries() { return this._m.boundaries; }

	// legacy is lit by the host's scene light (MeshLambert); no per-frame
	// uniforms to drive. Present for contract parity — a no-op here.
	update(_frame) {}

	// ── tile services (data only; the host owns the Raycaster) ──────────────
	// The host raycasts object3d, then passes the THREE hit's faceIndex here.
	tileFromFace(faceIndex) {
		if (faceIndex == null) return -1;
		const t = this._m.faceIndex[faceIndex];
		return t == null ? -1 : t;
	}

	tileNeighbors(i) { return this._m.neighbors[i]; }

	/** mesh-local (child-of-object3d) draped center of tile i. */
	getTileCenter(i, out = new Vector3()) {
		const c = this._m.drapedCenters[i];
		return out.set(c[0], c[1], c[2]);
	}

	/** unit surface normal of tile i (radial). */
	getTileNormal(i, out = new Vector3()) {
		const c = this._m.centers[i];
		return out.set(c[0], c[1], c[2]).normalize();
	}

	getTileTransform(i) {
		const position = this.getTileCenter(i);
		const normal = this.getTileNormal(i);
		const quaternion = new Quaternion().setFromUnitVectors(_UP, normal);
		return { position, normal, quaternion };
	}

	/**
	 * Parent obj onto tile i (mesh-local, so it inherits the planet's scale).
	 * @param {number} i
	 * @param {import('three').Object3D} obj
	 * @param {{offset?:number, orient?:boolean}} [o]
	 */
	placeOnTile(i, obj, { offset = 0, orient = true } = {}) {
		const { position, normal, quaternion } = this.getTileTransform(i);
		if (offset) position.addScaledVector(normal, offset);
		obj.position.copy(position);
		if (orient) obj.quaternion.copy(quaternion);
		this._m.group.add(obj);
		return obj;
	}

	setGridVisible(v) {
		if (v && !this._grid) {
			this._grid = this._m.buildGrid();
			this._m.group.add(this._grid);
		}
		if (this._grid) this._grid.visible = !!v;
	}

	// legacy has no incremental patch path — mutate the spec and rebuild.
	// Returns the NEW object3d; the host must re-add it (the old group is
	// disposed). Mirrors how the game's sector-map rebuilds on patch change.
	async applyPatches(patches) {
		this.spec = { ...this.spec, patches: { ...(this.spec.patches || {}), ...patches } };
		const old = this._m?.group;
		this._grid = null;
		await this.build();
		if (old) { old.parent?.remove(old); disposeObject(old); }
		return this;
	}

	dispose() {
		if (this._m?.group) disposeObject(this._m.group);
		this._m = null;
		this._grid = null;
	}
}

// low-level escape hatch for callers that want the raw mesh (the game's
// sector-map currently consumes this shape directly).
export { buildPlanetMesh } from './planet-3rdgen.js';
export default Planet;
